import React from 'react'
import { Typography, Box, useTheme } from '@material-ui/core'
import { CheckCircle as CheckIcon, Refresh as RefreshIcon } from '@material-ui/icons'
import { motion } from 'framer-motion'
import { StyledButton } from '../styles/StyledComponents'
import { useNavigate } from 'react-router-dom'
import { alpha } from '@material-ui/core/styles'

// notStartedText: the server's reason when the start was refused before anything
// was created or charged (for example a panel whose chemistry cannot be analysed).
const SuccessScreen = ({
  onStartNew,
  submittedFile,
  analysisType,
  newBalance,
  deductedAmount,
  insufficientNeeded,
  notStartedText,
}) => {
  const theme = useTheme()

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 350,
        damping: 25,
        mass: 0.8,
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 25,
        mass: 0.7,
      },
    },
  }

  const checkIconVariants = {
    hidden: { opacity: 0, scale: 0, rotate: -180 },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 20,
        mass: 1,
        delay: 0.2,
      },
    },
  }

  const pulseVariants = {
    pulse: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  }

  const getFileName = () => {
    return submittedFile?.sample_name || submittedFile?.original_name || 'Analysis'
  }

  const isRefused = Boolean(notStartedText) && insufficientNeeded == null
  const isInsufficient = insufficientNeeded != null || isRefused
  const navigate = useNavigate()

  const handleGoToAddBalance = () => {
    navigate('/libra/user/list')
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        width: '100%',
        maxWidth: '500px',
        margin: '0 auto',
      }}
    >
      {/* Success/Error Icon with Pulse Animation */}
      <motion.div
        variants={checkIconVariants}
        animate="pulse"
        style={{
          position: 'relative',
          marginBottom: '32px',
        }}
      >
        {/* Background Glow */}
        <motion.div
          animate={{
            boxShadow: isInsufficient
              ? [
                  `0 0 20px ${alpha(theme.palette.error.main, 0.19)}`,
                  `0 0 40px ${alpha(theme.palette.error.main, 0.13)}`,
                  `0 0 20px ${alpha(theme.palette.error.main, 0.19)}`,
                ]
              : [
                  `0 0 20px ${alpha(theme.palette.success.main, 0.19)}`,
                  `0 0 40px ${alpha(theme.palette.success.main, 0.13)}`,
                  `0 0 20px ${alpha(theme.palette.success.main, 0.19)}`,
                ],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: isInsufficient
              ? `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)} 0%, ${alpha(
                  theme.palette.error.main,
                  0.02,
                )} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(
                  theme.palette.success.main,
                  0.02,
                )} 100%)`,
          }}
        />

        {/* Check Icon */}
        <motion.div
          variants={pulseVariants}
          animate="pulse"
          style={{
            position: 'relative',
            zIndex: 1,
            width: '96px',
            height: '96px',
            borderRadius: '50%',
            background: isInsufficient
              ? `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`
              : `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.25)}, 0 4px 16px rgba(0, 0, 0, 0.1)`,
          }}
        >
          <CheckIcon
            sx={{
              fontSize: '48px',
              color: 'white',
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
            }}
          />
        </motion.div>
      </motion.div>

      {/* Header Message */}
      <motion.div variants={itemVariants}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '1.6rem', md: '2rem' },
            color: isInsufficient ? theme.palette.error.main : theme.palette.text.primary,
            mb: 1,
            letterSpacing: '0.5px',
          }}
        >
          {isRefused ? 'Analysis not started' : isInsufficient ? 'Insufficient Balance' : 'Analysis Submitted!'}
        </Typography>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1.1rem',
            color: theme.palette.text.secondary,
            mb: 3,
            lineHeight: 1.6,
            maxWidth: '400px',
          }}
        >
          {isInsufficient ? 'Your ' : 'Your '}
          {analysisType?.toLowerCase() || 'genomic'} analysis for{' '}
          <Box
            component="span"
            sx={{
              fontFamily: 'fontFamilyMono',
              fontWeight: 600,
              color: theme.palette.primary.main,
              backgroundColor: `${alpha(theme.palette.primary.main, 0.06)}`,
              px: 1,
              py: 0.5,
              borderRadius: 1,
            }}
          >
            {getFileName()}
          </Box>{' '}
          {isRefused
            ? 'was not started. Nothing was charged.'
            : isInsufficient
            ? 'could not be started due to insufficient balance.'
            : 'has been successfully submitted for processing.'}
        </Typography>
      </motion.div>

      {/* Status or Error Information */}
      <motion.div variants={itemVariants}>
        <Box
          sx={{
            p: 2.5,
            backgroundColor: isInsufficient
              ? `${alpha(theme.palette.error.main, 0.03)}`
              : `${alpha(theme.palette.info.main, 0.03)}`,
            borderRadius: '16px',
            border: isInsufficient
              ? `1px solid ${alpha(theme.palette.error.main, 0.13)}`
              : `1px solid ${alpha(theme.palette.info.main, 0.13)}`,
            mb: 4,
            maxWidth: '420px',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: isInsufficient ? theme.palette.error.main : theme.palette.info.main,
              fontWeight: 600,
              mb: 1,
              fontSize: '0.9rem',
            }}
          >
            {isInsufficient ? 'How to proceed' : 'What happens next?'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.85rem',
              lineHeight: 1.5,
            }}
          >
            {isRefused ? (
              <Box component="span" data-testid="start-refused-text">
                {notStartedText}
              </Box>
            ) : isInsufficient ? (
              <>
                You do not have enough balance to start this analysis. Please top up your account by{' '}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {insufficientNeeded}
                </Box>{' '}
                credit(s), then try again.
              </>
            ) : (
              <>
                Your analysis is now in the processing queue. You'll receive notifications as it progresses through each
                stage. You can monitor the status in your analysis dashboard.
              </>
            )}
          </Typography>
        </Box>
      </motion.div>

      {/* Payment Summary (optional) */}
      {(deductedAmount != null || newBalance != null) && (
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              p: 2.5,
              backgroundColor: `${alpha(theme.palette.success.main, 0.04)}`,
              borderRadius: '16px',
              border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
              mb: 4,
              maxWidth: '420px',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.success.main,
                fontWeight: 600,
                mb: 1,
                fontSize: '0.9rem',
              }}
            >
              Payment summary
            </Typography>
            {deductedAmount != null && (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.9rem', mb: 0.5 }}>
                Deducted:{' '}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {deductedAmount}
                </Box>{' '}
                credit(s)
              </Typography>
            )}
            {newBalance != null && (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.9rem' }}>
                New balance:{' '}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {newBalance}
                </Box>{' '}
                credit(s)
              </Typography>
            )}
          </Box>
        </motion.div>
      )}

      {/* Action Button */}
      <motion.div
        variants={itemVariants}
        whileHover={{
          scale: 1.02,
          transition: { type: 'spring', stiffness: 400, damping: 20 },
        }}
        whileTap={{
          scale: 0.98,
          transition: { type: 'spring', stiffness: 600, damping: 15 },
        }}
      >
        <StyledButton
          variant="contained"
          size="large"
          onClick={isInsufficient && !isRefused ? handleGoToAddBalance : onStartNew}
          startIcon={<RefreshIcon />}
          sx={{
            minWidth: '200px',
            py: 1.5,
            fontSize: '1rem',
            borderRadius: '12px',
            background: isInsufficient
              ? `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.21)}`,
            '&:hover': {
              background: isInsufficient
                ? `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 100%)`
                : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
            },
          }}
        >
          {isInsufficient && !isRefused ? 'Add Balance' : 'Start New Analysis'}
        </StyledButton>
      </motion.div>

      {/* Subtle Animation Elements */}
      <motion.div
        animate={{
          opacity: [0.3, 0.6, 0.3],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '4px',
          height: '4px',
          borderRadius: '50%',
          backgroundColor: theme.palette.primary.main,
        }}
      />

      <motion.div
        animate={{
          opacity: [0.2, 0.5, 0.2],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1,
        }}
        style={{
          position: 'absolute',
          top: '60%',
          right: '15%',
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          backgroundColor: theme.palette.secondary.main,
        }}
      />
    </motion.div>
  )
}

export default SuccessScreen
